import React, { useRef, useState, useEffect } from 'react';
import { Card, Row, Col } from 'react-bootstrap';
import Timeline from 'components/base/Timeline';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faVolumeControlPhone } from '@fortawesome/free-solid-svg-icons';
import CustomAudioControl from 'components/audio/CustomAudioControl';
import { NoTranscriptionCard } from 'components/cards/NoTranscriptionCard';
import { FetchHVACallRecording } from 'services/hvacallingmanagment.service';
import { fetchHVACallTranscription } from 'services/hvaqa.service';
import { CallRiskAnalysis } from 'pages/hvaqualityassurance/CallRiskAnalysis';
import HVASpinner from 'components/base/HVASpinner';

interface TranscriptItem {
  speaker: string;
  time: number; // Tiempo en segundos
  word: string;
}

interface AudioPlayerProps {
  callid: string;
  campaignid: number;
  customer: string;
  phone: string;
  audioSrc: string;
}

const AudioPlayer: React.FC<AudioPlayerProps> = ({
  callid,
  campaignid,
  customer,
  phone,
  audioSrc
}) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [currentWordIndex, setCurrentWordIndex] = useState<number>(0);
  const [transcriptData, setTranscriptData] = useState<TranscriptItem[]>();
  const [blobAudioSrc, setBlobAudioSrc] = useState<string>(''); // Estado para la URL del blob de audio
  const [isAudioAvailable, setIsAudioAvailable] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [summaryResponse, setSummaryResponse] = useState<string>('');
  const transcriptContainerRef = useRef<HTMLDivElement>(null);
  const currentWordRef = useRef<HTMLSpanElement>(null);

  // Función para convertir milisegundos a segundos
  const convertMsToSeconds = (ms: number) => ms / 1000;

  useEffect(() => {
    const fetchAudio = async () => {
      if (!audioSrc) {
        console.error('No audioSrc provided');
        setIsAudioAvailable(false);
        return;
      }

      try {
        const audioBlob = await FetchHVACallRecording({
          campaign: campaignid,
          callfilepath: audioSrc
        });

        if (audioBlob.size === 0) {
          setIsAudioAvailable(false);
          return;
        }

        const audioUrl = URL.createObjectURL(audioBlob);
        setBlobAudioSrc(audioUrl);
        setIsAudioAvailable(true);
      } catch (error) {
        console.error('Error fetching audio:', error);
        setIsAudioAvailable(false);
      }
    };

    fetchAudio();
  }, [campaignid, audioSrc]);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true); // Comenzar la carga
      try {
        const data = await fetchHVACallTranscription({
          id: callid,
          campaignid: campaignid
        });

        const transcription = JSON.parse(data.transcription);
        const summary = data.summary ? JSON.parse(data.summary) : null;
        if (summary && summary.response) {
          setSummaryResponse(summary.response);
        }

        // eslint-disable-next-line
        const words = transcription.words.map((word: any): TranscriptItem => ({
            // eslint-disable-next-line
            speaker: word.speaker === 'A' ? 'Speaker1' : word.speaker === 'B' ? 'Speaker2' : word.speaker,
            time: convertMsToSeconds(word.start),
            word: word.text
          })
        );

        setTranscriptData(words);
      } catch (error) {
        console.error('Error fetching transcription:', error);
        setTranscriptData([]);
        setSummaryResponse('');
      } finally {
        setIsLoading(false); // Finalizar la carga, independientemente del resultado
      }
    };

    fetchData();
  }, [callid, campaignid]);

  // Reiniciar la reproducción del audio al cambiar callid o campaignid
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.pause(); // Pausa el audio si se está reproduciendo
      audioRef.current.currentTime = 0; // Reinicia la posición del audio al comienzo
    }
  }, [callid, campaignid]);

  const handleTimeUpdate = (currentTime: number) => {
    const wordIndex = transcriptData?.findIndex((item, index) => {
      const nextItem = transcriptData[index + 1];
      return (
        currentTime >= item.time && (!nextItem || currentTime < nextItem.time)
      );
    });
    // eslint-disable-next-line
    if (wordIndex !== -1 && wordIndex !== undefined && wordIndex !== currentWordIndex) {
      setCurrentWordIndex(wordIndex ?? 0); // Utiliza un valor predeterminado si es undefined
    }
  };

  const handleWordClick = (time: number) => {
    if (audioRef.current) {
      audioRef.current.currentTime = time; // Cambia el tiempo del audio
      audioRef.current.play(); // Empieza la reproducción
    }
  };

  // Scroll automático
  useEffect(() => {
    if (currentWordRef.current && transcriptContainerRef.current) {
      const container = transcriptContainerRef.current;
      const currentWord = currentWordRef.current;

      const containerTop = container.getBoundingClientRect().top;
      const wordTop = currentWord.getBoundingClientRect().top;
      // eslint-disable-next-line
      const scrollPosition = container.scrollTop + (wordTop - containerTop) - container.clientHeight / 2;

      container.scrollTo({
        top: scrollPosition,
        behavior: 'smooth'
      });
    }
  }, [currentWordIndex]);

  const groupedTranscript = transcriptData
    ? transcriptData.reduce<
        {
          speaker: string;
          time: number;
          words: { text: string; index: number; time: number }[];
        }[]
      >((acc, item, index) => {
        const lastGroup = acc[acc.length - 1];

        if (lastGroup && lastGroup.speaker === item.speaker) {
          lastGroup.words.push({ text: item.word, index, time: item.time });
        } else {
          acc.push({
            speaker: item.speaker,
            time: item.time,
            words: [{ text: item.word, index, time: item.time }]
          });
        }

        return acc;
      }, [])
    : [];

  function formatTime(seconds: number): string {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);

    const formattedMinutes = String(minutes).padStart(2, '0');
    const formattedSeconds = String(remainingSeconds).padStart(2, '0');

    return `${formattedMinutes}:${formattedSeconds}`;
  }

  return (
    <Row className="p-0 mx-0 rounded border">
      <Col className="px-3">
        <h5 className="pt-3 mb-3">Quality Assurance analysis</h5>
        <p className="fs-9 fw-bold text-body-emphasis mb-1">Call summary</p>
        <div
          className="fs-9 scrollbar lh-sm"
          style={{
            maxHeight: '96px',
            height: '96px'
          }}
        >
          {summaryResponse ? summaryResponse : 'Call summary not available.'}
        </div>

        {/* Espacio para el análisis de riesgos */}
        <div className="mt-3">
          <p className="fs-9 fw-bold text-body-emphasis mb-1">Risk Analysis</p>
          <CallRiskAnalysis
            callid={callid}
            campaignid={campaignid}
            componentHeight="120px"
          />
        </div>
      </Col>
      <Col className="px-0 border-start">
        <Card className="h-100 border-0">
          <Card.Body className="d-flex flex-column p-0">
            <Card.Title className="pb-2 border-bottom text-center">
              <p className="fs-9 fw-bold text-body-emphasis pt-3 mb-1">
                {customer}
              </p>
              <p className="fs-10 fw-normal lh-s mb-0">{phone}</p>
              {isAudioAvailable ? (
                <CustomAudioControl
                  key={`${callid}-${campaignid}`}
                  audioSrc={blobAudioSrc}
                  onTimeUpdate={handleTimeUpdate}
                  audioRef={audioRef}
                />
              ) : (
                <p className="fs-9 pt-2 fw-normal mb-2 text-danger">
                  Audio file not available
                </p>
              )}
            </Card.Title>
            {isLoading ? (
              <div className="d-flex justify-content-center align-items-center h-100 pb-6">
                <HVASpinner />
              </div>
            ) : transcriptData && transcriptData.length > 0 ? (
              <div
                className="h-100 px-3 pt-0 mb-2 scrollbar hvasystem-transcription-QA"
                ref={transcriptContainerRef}
                style={{ maxHeight: '315px', height: '315px' }}
              >
                <Timeline variant="vertical">
                  {groupedTranscript.map((item, index) => (
                    <Timeline.Item className="position-relative" key={index}>
                      <Row>
                        <Col xs={2} className="d-flex timelinecol-fixed">
                          <Timeline.OppositeContent>
                            <p className="fs-9 fw-bold pb-0 text-end lh-sm">
                              {item.speaker}
                              <span className="fs-10 pt-0 fw-normal">
                                <br className="d-none d-md-block" />{' '}
                                {formatTime(item.time)}
                              </span>
                            </p>
                          </Timeline.OppositeContent>
                          <Timeline.Separator className="position-md-relative">
                            <Timeline.Dot className="bg-body-emphasis">
                              <FontAwesomeIcon
                                icon={faVolumeControlPhone}
                                className="text-primary-dark fs-8"
                              />
                            </Timeline.Dot>
                            {index !== groupedTranscript.length - 1 && (
                              <Timeline.Bar className="h-100 border-dashed" />
                            )}
                          </Timeline.Separator>
                        </Col>
                        <Col className="flex-grow-1">
                          <Timeline.Content className="p-0 text-start">
                            <p
                              className={`fs-9 lh-sm ${
                                index !== groupedTranscript.length - 1
                                  ? 'mb-3'
                                  : 'mb-0'
                              }`}
                            >
                              {item.words.map(word => (
                                <span
                                  key={word.index}
                                  // eslint-disable-next-line
                                  ref={word.index === currentWordIndex ? currentWordRef : null} // Asignar la referencia
                                  onClick={() => handleWordClick(word.time)} // Controlador de clic en la palabra
                                  style={{ cursor: 'pointer' }}
                                  className={`${
                                    // eslint-disable-next-line
                                    word.index === currentWordIndex ? 'text-primary bg-body' : ''
                                  }`}
                                >
                                  {word.text + ' '}
                                </span>
                              ))}
                            </p>
                          </Timeline.Content>
                        </Col>
                      </Row>
                    </Timeline.Item>
                  ))}
                </Timeline>
              </div>
            ) : (
              <NoTranscriptionCard />
            )}
          </Card.Body>
        </Card>
      </Col>
    </Row>
  );
};

export default AudioPlayer;
